import React from 'react'
import { useTranslation } from 'next-i18next'
import { FormikProps } from 'formik'

import { getFormattedStates } from '@/utils/address'
import useBasic from '@/hooks/use-basic'
import { FieldDropdown, FieldInput } from '@/components/form'
import { InfoCircleGrey } from '@/components/icons'

import {
  Divider,
  IconWrapper,
  StyledGroup,
  StyledRow,
  ToggleGroup,
  ToggleLabel
} from '../../styles/steps.styles'
import { Listing } from '../../types/Listing'
import FieldSwitchToggle from '../field-switch-toggle'
import { show as showInteractiveModal } from '../interactive-modal'
import MapComponent from './map'

type FieldValues = Listing & { building_id: string | null }
type Props = {
  formikProps: FormikProps<FieldValues>
}

const PropertyLocation = ({ formikProps }: Props) => {
  const { values, setFieldValue } = formikProps

  const { t } = useTranslation()
  const isHouse = values.type === 'house'
  const isApartment = values.type === 'condo'
  const { countries } = useBasic(['countries'])

  const isNewAddress = values.building_id === null

  const propertyNameLabel: any = {
    condo: `${t('l.listing.create.building_name.label', 'Building name')}`,
    house: t('l.listing.create.house_name.label', 'House name'),
    townhouse: t('l.listing.create.townhouse_name.label', 'Townhouse name')
  }

  const countryOptions = [
    { value: 'ca', label: t('Canada') },
    { value: 'us', label: t('United States') }
  ]

  const isUnitedStatesSelected = values.country === 'us'

  return (
    <>
      <StyledRow>
        <StyledGroup col={12}>
          <FieldDropdown
            name="country"
            id="country"
            placeholder={t('common.country', 'Country')}
            options={countryOptions}
            isDisabled={!isNewAddress}
            defaultValue={{ value: 'ca', label: 'Canada' }}
            required
            onChange={() => setFieldValue('state', '')}
          />
        </StyledGroup>
      </StyledRow>

      <StyledRow>
        <StyledGroup col={6}>
          <FieldInput
            type="text"
            name="suite"
            id="suite"
            hideError={!isApartment}
            required={isApartment}
            placeholder={
              isApartment
                ? t('l.listing.create.unit_number.label', 'Unit number')
                : t(
                    'l.listing.create.unit_number_house.label',
                    'Unit number (if applicable)'
                  )
            }
          />
        </StyledGroup>

        <StyledGroup col={6}>
          <FieldInput
            name="full_street_name"
            id="street_name"
            disabled={values.building_id !== null}
            placeholder={t('common.street_address', 'Street address')}
            required
          />
        </StyledGroup>
      </StyledRow>

      <>
        <Divider />
        <StyledRow>
          <StyledGroup col={6}>
            <FieldInput
              name="building_name"
              id="building_name"
              disabled={values.building_id !== null && isApartment}
              placeholder={propertyNameLabel[values.type]}
              required={isApartment}
            />
          </StyledGroup>
          <StyledGroup col={6}>
            <FieldInput
              name="city"
              id="city"
              disabled={values.building_id !== null}
              placeholder={t('common.city', 'City')}
              required
            />
          </StyledGroup>
        </StyledRow>
        <StyledRow>
          <StyledGroup col={6}>
            <FieldDropdown
              name="state"
              id="state"
              placeholder={
                isUnitedStatesSelected
                  ? t('r.profile.edit.state.label', 'State')
                  : t('common.province', 'Province')
              }
              options={getFormattedStates(countries, values?.country || 'ca')}
              isDisabled={values.building_id !== null}
              isSearchable
              required
            />
          </StyledGroup>
          <StyledGroup col={6}>
            <FieldInput
              name="zip"
              id="zip"
              disabled={values.building_id !== null}
              placeholder={
                isUnitedStatesSelected
                  ? t('common.zip_code', 'Zip code')
                  : t('common.postal_code', 'Postal code')
              }
              required
            />
          </StyledGroup>
        </StyledRow>
        <StyledRow>
          <ToggleGroup col={12}>
            <FieldSwitchToggle
              name="hide_unit_number"
              id="hide_unit_number"
              aria-labelledby="unit-number-label"
              style={{ marginRight: '10px' }}
            />
            <ToggleLabel
              htmlFor="hide_unit_number"
              strong
              id="unit-number-label"
              style={{ fontWeight: '400' }}
            >
              {!isHouse
                ? t(
                    'l.listing.create.hide_unit_number_listing.label',
                    'Hide unit number from listing'
                  )
                : t(
                    'l.listing.create.hide_street_number_listing.label',
                    'Hide unit and street number from listing'
                  )}
            </ToggleLabel>
            <IconWrapper
              onClick={() => showInteractiveModal({})}
              style={{ cursor: 'pointer' }}
            >
              <InfoCircleGrey width={20} />
            </IconWrapper>
          </ToggleGroup>
        </StyledRow>
      </>

      <MapComponent building={values?.building} />
    </>
  )
}

export default PropertyLocation
